import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ConvertHelper } from '@barthauer/abc/core';
import { DataService, QueryObjectsModel, ReadDocumentModel } from '@barthauer/abc/core/data';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { DashboardControl, ResourceManager, WorkingMode } from 'devexpress-dashboard';
import { ParameterService } from 'libs/abc/queries/parameters/values-editor/src/lib/services/parameter.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'abc-dashboard',
  template: `<div [class]="'dashboard-container'"></div>`,
  styleUrls: ['./abc-dashboard.component.scss'],
  standalone: true,
})
export class _AbcDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() onInit: EventEmitter<void> = new EventEmitter();
  @Input() endPoint: string;
  @Input() workingMode: WorkingMode;
  @Input() ajaxHeaders: { [key: string]: any };
  @Input() autoInitialize: boolean = true;
  @Input() ignoreParameterDefinitions: boolean = false;
  @Input() mobileLayoutEnabledInput: string = 'Auto';
  @Input() set initialDashboardId(value: string) {
    this._initialDashboardId = value;
    if (this.dashboardControl) {
      this.refreshControl();
    }
  }
  get initialDashboardId(): string {
    return this._initialDashboardId;
  }

  get isInitialized(): boolean {
    return this.dashboardControl !== undefined;
  }

  private _initialDashboardId: string;
  private dashboardControl!: DashboardControl;
  private styleableMutationObserver: MutationObserver;
  private styleableHtmlElements: HTMLElement[] = [];
  private readonly parameterPrefix = 'P$';

  constructor(
    private element: ElementRef,
    private coreDataService: DataService,
    private parameterService: ParameterService,
    private resourceManagerService: ResourceManagerService,
  ) {
    ResourceManager.embedBundledResources();
    this.applyHtmlStyle(element.nativeElement);
  }

  async ngOnInit() {
    await this.resourceManagerService.getResourceSet('dx-dashboard', undefined, undefined, (data) =>
      ResourceManager.setLocalizationMessages(data),
    );
    await this.resourceManagerService.getResourceSet('dx-analytics-core', undefined, undefined, (data) =>
      ResourceManager.setLocalizationMessages(data),
    );
    this.onInit.emit();
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.autoInitialize) {
      await this.refreshControl();
    }
  }

  ngOnDestroy(): void {
    this.dashboardControl?.dispose();
  }

  public async setEndPoint(endPoint: string, ajaxHeaders: { [key: string]: any }): Promise<void> {
    this.endPoint = endPoint;
    this.ajaxHeaders = ajaxHeaders;

    await this.refreshControl();
  }

  private async refreshControl(): Promise<void> {
    if (!this.ignoreParameterDefinitions) {
      await this.handleParameterDefinitions();
    }

    this.dashboardControl?.dispose();
    const dashboardContainer: HTMLElement = this.element.nativeElement.querySelector('.dashboard-container');
    dashboardContainer.innerHTML = '';
    this.dashboardControl = new DashboardControl(dashboardContainer, {
      endpoint: this.endPoint ?? 'https://demos.devexpress.com/services/dashboard/api',
      workingMode: this.workingMode ?? 'ViewerOnly',
      initialDashboardId: this.initialDashboardId,
      ajaxRemoteService: {
        headers: this.ajaxHeaders,
      },
      extensions: {
        'mobile-layout': {
          mobileLayoutEnabled: this.mobileLayoutEnabledInput,
        },
        'dashboard-export': {
          allowExportDashboard: true,
          allowExportDashboardItems: true,
        },
      },
    });
    this.dashboardControl.render();
  }

  private async handleParameterDefinitions(): Promise<void> {
    try {
      const readDocumentModel = new ReadDocumentModel();
      readDocumentModel.Id = this.initialDashboardId;

      const queryObjectsModel: QueryObjectsModel = ConvertHelper.arrayBufferToObject<QueryObjectsModel>(
        await firstValueFrom(this.coreDataService.executeReadQueryFromDocumentQuery(readDocumentModel)),
      );

      if (queryObjectsModel?.ParameterDefinitions?.length) {
        const parameterValues: ParameterValue[] = await this.parameterService.fillParametersIfExist(
          queryObjectsModel.ParameterDefinitions,
        );

        for (let parameterValue of parameterValues) {
          this.ajaxHeaders[this.parameterPrefix + parameterValue.Name] = parameterValue.Value;
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  private applyHtmlStyle(domElement: HTMLElement) {
    this.styleableHtmlElements = [];
    if (!this.styleableMutationObserver) {
      let isMutating: boolean = false;
      this.styleableMutationObserver = new MutationObserver((arr) => {
        if (
          !isMutating &&
          arr?.find((_) => _.target instanceof HTMLElement && !this.styleableHtmlElements.includes(_.target))
        ) {
          isMutating = true;
          try {
            this.applyAriaLabelText(Array.from(domElement.getElementsByClassName('dx-tab')));
          } finally {
            isMutating = false;
          }
        }
      });
    }

    this.styleableMutationObserver.observe(domElement, {
      subtree: true,
      childList: true,
    });
  }

  private applyAriaLabelText(elements: Node[]) {
    if (elements) {
      for (const element of elements) {
        if (element instanceof HTMLElement) {
          const label = element.getAttribute('aria-label');
          if (label) {
            if (!this.styleableHtmlElements.includes(element)) {
              this.styleableHtmlElements.push(element);
            }
            element.innerText = label;
          } else {
            this.applyAriaLabelText(Array.from(element.childNodes));
          }
        }
      }
    }
  }
}
